/**
 * Shared changed-fields diff helper for *.updated platform events.
 *
 * Task 4050b: a single source of truth for deriving "what changed" from
 * `data.previous_attributes` + `data.object`. Consumed by:
 * - the event-card summary (`eventFormatters.ts` customerFormatter), and
 * - the `ChangedFields` detail component rendered by both
 *   `EventDetailDrawer` (Payload tab) and the legacy `EventDetailModal`.
 *
 * The helper is generic across event families — any event that carries a
 * non-empty `previous_attributes` map produces rows, not just
 * `customer.updated` (the same missing-change-data problem affects every
 * `*.updated` type; see docs/tasks/4050b-stream-event-detail-change-clarity.md).
 */
import type { PlatformEvent } from '../types'

/** snake_case / kebab-case -> Title Case. */
export function titleCase(value: string): string {
  return value
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/** One changed attribute: previous value (from `previous_attributes`) and
 * the new value (read from `data.object` by the same key). */
export interface ChangedField {
  /** Raw attribute key, e.g. `external_identifiers`. */
  field: string
  /** Human-readable label, e.g. `External Identifiers`. */
  label: string
  /** The value before the change (as recorded by the emitting service). */
  previous: unknown
  /** The value after the change, read from the event's object snapshot.
   * `undefined` when the snapshot is missing or does not carry the key. */
  next: unknown
}

/**
 * Derive the changed-fields diff for an event.
 *
 * Returns `[]` when `data.previous_attributes` is null, empty, or not an
 * object — callers render an explicit empty state in that case rather than
 * hiding the section.
 */
export function buildChangedFields(event: PlatformEvent): ChangedField[] {
  const previousAttrs = event.data?.previous_attributes
  if (!previousAttrs || typeof previousAttrs !== 'object' || Array.isArray(previousAttrs)) {
    return []
  }

  const object = event.data?.object
  const snapshot =
    object && typeof object === 'object' && !Array.isArray(object)
      ? (object as Record<string, unknown>)
      : null

  return Object.keys(previousAttrs).map((field) => ({
    field,
    label: titleCase(field),
    previous: (previousAttrs as Record<string, unknown>)[field],
    // Own-property check: a plain `snapshot[field]` would walk the prototype
    // chain for keys like `constructor`/`toString`/`hasOwnProperty` and leak
    // an inherited function as the "next" value (hardening review 2026-06-11).
    next:
      snapshot && Object.prototype.hasOwnProperty.call(snapshot, field)
        ? snapshot[field]
        : undefined,
  }))
}
